import { Box, InputLabel, Stack, TextField } from "@mui/material";
import { MprcContainer, MprcRequestErrorReport, MprcRequestIndicator } from "components";
import { ButtonSave } from "components/buttons";
import { AutoCompleteDataModel } from "components/MprcAutoComplete";
import { createAutoCompleteModel } from "components/MprcAutoComplete/AutoCompleteDataModel";
import { DirectMessageListResponse } from "generated/CoreApi_v1";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { UserAutoComplete } from "views/foundation/user/components/UserAutoComplete";

/////////////////////////////////////////////////////////////////////////////////

export interface INewDirectMessage {
    action: "reply" | "forward";
    message: DirectMessageListResponse | null;
}

interface IProps {
    allowSelectRecipients?: boolean;
    initialRecipients?: AutoCompleteDataModel[];
    newMessage?: INewDirectMessage | null;
    onPost?: (subject: string, body: string) => Promise<boolean>;
    onSend?: () => void;
}

export const NewDirectMessage = ({ initialRecipients = [], newMessage, allowSelectRecipients, onPost, onSend }: IProps) => {
    const api = useCoreApi();
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const [saving, setSaving] = useState<boolean>(false);
    const [viewError, setViewError] = useState<any>();
    const [recipients, setRecipients] = useState<AutoCompleteDataModel[]>(initialRecipients);
    const [subject, setSubject] = useState<string>("");
    const [body, setBody] = useState<string>("");
    const recipientRef = useRef<HTMLInputElement>(null);
    const bodyRef = useRef<HTMLInputElement>(null);

    ///
    function clearFields() {
        setRecipients([]);
        setSubject("");
        setBody("");
    }

    // Ao salvar
    useEffect(() => {
        async function postData() {
            try {
                if (onPost)
                    await onPost(subject, body);
                else
                    await api.v1.directMessage.sendDirectMessage({
                        recipientIds: recipients.map(x => x.key),
                        subject,
                        body,
                        attachId: null
                    });
                enqueueSnackbar(t("DirectMessage.Edit.SuccessMessage"), { variant: "success" });
                clearFields();
                if (onSend) onSend();
            }
            catch (e) {
                setViewError(e);
            }
            setSaving(false);
        }

        if (saving) postData();

    }, [saving]);

    ///
    useEffect(() => {
        if (newMessage) {
            const msg = newMessage.message;
            if (msg != null) {
                if (newMessage.action == "reply") {
                    setRecipients([createAutoCompleteModel(msg.sender.userId, msg.sender.name, msg.sender.thumbnailUrl)]);
                    setTimeout(() => {
                        bodyRef.current?.setSelectionRange(0, 0);
                        bodyRef.current?.focus();
                    }, 500);
                } else {
                    recipientRef.current?.focus();
                }
                setSubject(msg.subject);
                setBody(`\n${'-'.repeat(90)}\n${msg.body}`);
            }
        } else if (newMessage != undefined && newMessage == null) {
            clearFields();
            setTimeout(() => {
                recipientRef.current?.focus();
            }, 500);
        }
    }, [newMessage]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////
    const canSave = (onPost || recipients.length > 0) && subject.length > 0 && body.length > 0;

    return (
        <Stack direction="column" width="100%" height="100%">

            <Box flexGrow={1}>
                <MprcContainer>
                    <MprcRequestErrorReport error={viewError} />
                    <MprcRequestIndicator type={"post"} visible={saving} />
                    {
                        !onPost && (allowSelectRecipients || recipients.length > 0) &&
                        <MprcContainer>
                            <InputLabel required>{t("DirectMessage.Edit.ToLabel")}</InputLabel>
                            <UserAutoComplete
                                autoFocus
                                inputRef={recipientRef}
                                selectedItems={recipients}
                                disabled={allowSelectRecipients === false}
                                multiSelect
                                required
                                onChange={(items: AutoCompleteDataModel[]) => setRecipients(items)}
                            />
                        </MprcContainer>
                    }
                    <MprcContainer>
                        <InputLabel required>{t("DirectMessage.Edit.SubjectLabel")}</InputLabel>
                        <TextField
                            required
                            autoFocus
                            fullWidth
                            autoComplete="off"
                            placeholder={t("DirectMessage.Edit.SubjectPlaceholder")}
                            margin="dense"
                            slotProps={{
                                input: {
                                    inputProps: { maxLength: 255 }
                                }
                            }}
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                        />
                    </MprcContainer>

                    <MprcContainer>
                        <InputLabel required>{t("DirectMessage.Edit.BodyLabel")}</InputLabel>
                        <TextField
                            required
                            fullWidth
                            autoComplete="off"
                            inputRef={bodyRef}
                            placeholder={t("DirectMessage.Edit.BodyPlaceholder")}
                            multiline
                            margin="dense"
                            variant="outlined"
                            rows={8}
                            value={body}
                            onChange={(e) => setBody(e.target.value)}
                        />
                    </MprcContainer>
                </MprcContainer>
            </Box>

            <Box alignSelf="end">
                <ButtonSave
                    text={t("DirectMessage.Edit.SendMessageButtonText")}
                    title={t("DirectMessage.Edit.SendMessageButtonTitle")}
                    onClick={() => setSaving(true)}
                    disabled={!canSave}
                />
            </Box>
        </Stack>
    );
};
